import { forEach, map, difference, assign } from 'lodash';

import { Tool } from '../view';

import { computeScale, FiguresHandles } from './select-transform';

import { Point, Matrix, Frame, Figures } from 'cdl';

const { dotProduct, substract } = Point;
const _abs = Math.abs;

export const ClipTool = Tool.extend({ typeName: 'ClipTool',

  type: 'Final',
  toolbarName: 'clipTool',
  allowKeyShortcuts: true,

  initTool( view, config ) {
    assign(this, config || {});
    this._data = null;

    this.handles = new FiguresHandles({ view, tool: this,

      editingItems: () => {
        const figure = this._selectedFigure;
        return figure ? [ figure ] : [];
      },

      itemsEnabledContentEdit( /* view, figures */ ) {
        return false;
      },


      commit$: () => {
      },

      itemsEnabledTransforms( view, figures ) {
        return {
          anisometricTransforms: view.are(figures,'aspectRatioChangeable'),
          translate: view.are(figures,'movable'),
          scale: view.are(figures,'scalable'),
          rotate: false,
          shear: false,
          contentEdit: false
        };
      },

      getOnTranslateValue({ view, docPointRaw, cmdKey, shiftKey }) {
        const { handles, _data, _downFrame } = this;
    
        const offset = substract( docPointRaw, handles.mouseDownPos );
        if( ! offset.isZero() ) {
          _data.started = true;
        }
    
        const snapTo = _data.snapTo;
        const alignmentLines = view._alignmentLines || { _visible: false, _snap: false };      
        const forceSnap = shiftKey || cmdKey;
        const al_visible = forceSnap || alignmentLines._visible;
        const al_snap    = forceSnap || alignmentLines._snap;
        const al_enabled = al_visible || al_snap;
        if( snapTo && al_enabled && _data.started ) { // snap
          const snapSize = view.viewDeltaToDoc(14);
          const { bounds } = _downFrame.translated(offset);
          let al_x = null, al_y = null, diff_x = Number.MAX_VALUE, diff_y = Number.MAX_VALUE;
          forEach( snapTo, snap => {
            forEach( snap, (s,name) => {
    
              const diff = substract( s, bounds[name] );
    
              const ax = _abs(diff.x);
              if( ax < snapSize && _abs(diff_x) > ax ) {
                diff_x = diff.x;
                if( al_visible ) {
                  al_x = s.x;
                }
              }
              const ay = _abs(diff.y);
              if( ay < snapSize && _abs(diff_y) > ay ) {
                diff_y = diff.y;
                if( al_visible ) {
                  al_y = s.y;
                }
              }
            });
          });
    
          handles.guideline_x = al_x;
          if( al_snap && diff_x !== Number.MAX_VALUE ) {
            offset.x += diff_x;
          }
    
          handles.guideline_y = al_y;
          if( al_snap && diff_y !== Number.MAX_VALUE ) {
            offset.y += diff_y;
          }
        }
        else {
          handles.guideline_x = null;
          handles.guideline_y = null;
        }
    
        //get the minimum and maximum translation, then fix the offset if it will go out of the parent frame
        let lParent = this.clipTool._selectedParent
        let lParentFrame = lParent.globalFrame_();
        let lFigureFrame = _downFrame.translated(offset);
        let lTransform = lParent.rwPowerClipTransform();
        let lInvTransform = Matrix.invert(lTransform);
        lParentFrame.transform(lInvTransform);
        lFigureFrame.transform(lInvTransform);

        let lDeltaP0 = substract(lParentFrame.p0, lFigureFrame.p0);
        if (lDeltaP0.x > 0)
        {
          let lP = new Point(lDeltaP0.x,0);
          lP.transform(lTransform);
          lP.translate(-lParent.pin.x,-lParent.pin.y)
          offset.x += lP.x;
          offset.y += lP.y;
        }
        if (lDeltaP0.y > 0)
        {
          let lP = new Point(0, lDeltaP0.y);
          lP.transform(lTransform);
          lP.translate(-lParent.pin.x,-lParent.pin.y)
          offset.x += lP.x;
          offset.y += lP.y;
        }

        let lDeltaP2 = substract(lParentFrame.p2, lFigureFrame.p2);
        if (lDeltaP2.x < 0)
        {
          let lP = new Point(lDeltaP2.x,0);
          lP.transform(lTransform);
          lP.translate(-lParent.pin.x,-lParent.pin.y)
          offset.x += lP.x;
          offset.y += lP.y;
        }
        if (lDeltaP2.y < 0)
        {
          let lP = new Point(0, lDeltaP2.y);
          lP.transform(lTransform);
          lP.translate(-lParent.pin.x,-lParent.pin.y)
          offset.x += lP.x;
          offset.y += lP.y;
        }

        return view.asFastOffset(offset);
      },

      getOnScaleValue({ view, event, docPoint, cmdKey, shiftKey }) {
        const { handles, _data, _downFrame } = this;
        const { current, mouseDownPos } = handles;
        const { fixedCenter, center, scalePin, scaleDir } = _data;
    
        const pin = scalePin;
    
        const scale = computeScale( pin, scaleDir, docPoint, mouseDownPos, view.viewDeltaToDoc(1) );
    
        const { name } = current;
        scale.sy = ( name === 'ne' || name === 'nw' || name === 'sw' || name === 'se' ) ? scale.sx : 1;

        let lRetry = 0;
        let lFailedToScale = true;
        let lParent = this.clipTool._selectedParent
        let lParentFrame = lParent.globalFrame_();
        let lTransform = lParent.rwPowerClipTransform();
        let lInvTransform = Matrix.invert(lTransform);
        lParentFrame.transform(lInvTransform);

        const lOldScaleFrom = Matrix.scaleFrom( scale.pin, _data.scale.sx, _data.scale.sy, scale.deg );
        let lOldFigureFrame = _downFrame.clone().transform(lOldScaleFrom);
        lOldFigureFrame.transform(lInvTransform);

        while (lRetry++ < 5 && lFailedToScale) {
          if (scale.sx <= 0 || scale.sy <= 0) {
            break;
          }
          lFailedToScale = false;

          const scaleFrom = Matrix.scaleFrom( scale.pin, scale.sx, scale.sy, scale.deg );
          let lFigureFrame = _downFrame.clone().transform(scaleFrom);
          lFigureFrame.transform(lInvTransform);

          let lFigureDeltaP0 = substract(lFigureFrame.p0, lOldFigureFrame.p0);
          let lFigureDeltaP2 = substract(lFigureFrame.p2, lOldFigureFrame.p2);

          let lDeltaP0 = substract(lParentFrame.p0, lFigureFrame.p0);
          if (lDeltaP0.x > 0 && lFigureDeltaP0.x < 0)
          {
            scale.sx = (_data.scale.sx+scale.sx)/2;
            scale.sy = (_data.scale.sy+scale.sy)/2;
            lFailedToScale = true;
          }
          if (lDeltaP0.y > 0 && lFigureDeltaP0.y < 0)
          {
            scale.sx = (_data.scale.sx+scale.sx)/2;
            scale.sy = (_data.scale.sy+scale.sy)/2;
            lFailedToScale = true;
          }
  
          let lDeltaP2 = substract(lParentFrame.p2, lFigureFrame.p2);
          if (lDeltaP2.x < 0 && lFigureDeltaP2.x > 0) 
          {
            scale.sx = (_data.scale.sx+scale.sx)/2;
            scale.sy = (_data.scale.sy+scale.sy)/2;
            lFailedToScale = true;
          }
          if (lDeltaP2.y < 0 && lFigureDeltaP2.y > 0)
          {
            scale.sx = (_data.scale.sx+scale.sx)/2;
            scale.sy = (_data.scale.sy+scale.sy)/2;
            lFailedToScale = true;
          }
          }
        if (lFailedToScale) {
          scale.sx = _data.scale.sx
          scale.sy = _data.scale.sy;
        }
        return scale;
      },
    
  });

    this.handles.clipTool = this;
  },

  link( view ) {
    view.on({ 'preview': this.on_preview }, this );

    this.handles.create();
  },

  unlink( view ) {
    const parent = this._selectedParent;
    if (parent) {
      parent.endEditClip$();
    }
    view.un(this);
    this.handles.dispose();
  },

  dispose() {
    this.handles.dispose();
    this.callParent();
  },

  draw( config ) {
    this.handles.draw(config);
  },

  on_keydown({ view, event, altKey, which }) {
    if( ! view._keyShortcuts ) {
      return;
    }

    switch( which ) {
      case 46: // 'Del'
      event.preventDefault();
      if( view.canRemove(this._selectedFigure) ) {

        this._selectedParent.clippedBy = "";
        view.remove(this._selectedFigure);
        this._selectedFigure = null;
        this._selectedParent = null;
        this.handles.update();
        return view.commit$();
      }
      break;
    }
  },

  on_pointerdown({ view, event, isPrimary, docPoint }) {
    if( ! isPrimary ) {
      return;
    }

    const { handles } = this;
    if( handles._editionMode ) {
      //this._endMode(event);
      return;
    }

    const leftClick = event.which === 1;
    if( ! leftClick ) {
      return;
    }

    if( handles.isHandle(event) ) {
      event.stopPropagation();
      handles.down(event);
    }
    else {
      let parent = this._selectedParent;
      if (parent) {
        parent.endEditClip$();
        this._selectedFigure = null;
        this._selectedParent = null;
        this.handles.update();
        //return;
      }
      
      parent = event.figure; // [EVENTS] view.figureAtPoint( docPoint ); // get top most figure at point
      if( parent ) { // click directly on figure (not handle)?

        event.stopPropagation();

        let [lClipContent, lClipParent] = parent.getClippedBy();

        if( lClipContent ) {
            parent.beginEditClip$().then( () => {
              this._figureClicked( lClipContent, parent );
              return view.render$();
            }).then( () => {
              handles.down(event);
            });
        }
        else if ( parent.isRaster ) {// TODO We are restricting to rasters now, but clipping can be done on any figure
            const lId = this._computeUniqueId();
            parent.addClippingRect$(lId).then( () => {
              [lClipContent, lClipParent] = parent.getClippedBy();
              this._figureClicked( lClipContent, parent );
              return view.render$();
            }).then( () => {
              handles.down(event);
            });
        }
      }
      else { // click on empty design area?

        this._selectedFigure = null;
        this._selectedParent = null;
        this.handles.update();
      }
    }
  },

  _computeUniqueId() {
    const lDoc = this.view.document;
    let lUniqueIdFound = false;
    let lUniqueId;
    for (let lUniqueIdx = 1; !lUniqueIdFound; lUniqueIdx++) {
      lUniqueId = "c" + lUniqueIdx;
      lUniqueIdFound = true;
      for( let i = 0; lUniqueIdFound && i < lDoc.figures.length; i++) {
        if (lDoc.figures[i].id == lUniqueId) {
          lUniqueIdFound = false;
        }
      }
    }
    return lUniqueId;
  },

  _figureClicked( figure, parent ) {

    this.view.activeDocument = parent.document;

    this._selectedFigure = figure;
    this._selectedParent = parent;

    this.handles.update();
    this.view.render$();
  },

  on_mousemove( event ) {
    this.handles.mousemove(event);
  },

  on_pointermove( event ) {
    this.handles.move(event);
  },

  on_pointerup( event ) {
    this.handles.up(event);
  },

  on_preview() {
    this.handles.update();
  },

  somethingToDraw() {
    return this.handles.visible;
  },

  needsToDrawFor( docIsland ) {
    const { frame } = this.handles;
    return !!frame && Frame.areOverlapping( docIsland.frame_(), this.handles.frame );
  }
});

Tool.Clip = ClipTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/clip.js
 **/